import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const emptyCrop = () => ({ crop_id: '', count: '', grade_a_kg: '', grade_b_kg: '', reject_kg: '', notes: '' })

const GRADE_GUIDE = {
  'Lettuce': {
    a: 'Grade A — 150–250g, full firm head, no blemishes, vibrant colour. Ready for premium sale.',
    b: 'Grade B — 100–149g or minor outer leaf blemish. Sell at discount to restaurants.',
    r: 'Reject — diseased, damaged, bolting, or under 100g. Do NOT sell. Dispose safely.',
  },
  'Strawberries': {
    a: 'Grade A — 15g+, 75%+ red, firm, calyx intact, no blemishes. Premium sale.',
    b: 'Grade B — 10–14g, minor blemish or slight softness. Sell at discount.',
    r: 'Reject — mouldy, bruised, under 10g, or fully soft. Do NOT sell.',
  },
  default: {
    a: 'Grade A — full size, perfect condition, no blemishes. Premium price.',
    b: 'Grade B — slightly small or minor cosmetic issue. Sell at discount.',
    r: 'Reject — diseased, damaged or unsaleable. Dispose safely.',
  }
}

export default function HarvestLog() {
  const { user, profile, canEditData } = useAuth()
  const [logs, setLogs] = useState([])
  const [activeCycles, setActiveCycles] = useState([])
  const [tab, setTab] = useState('new')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [form, setForm] = useState({
    harvest_date: ugandaDate(),
    cycle_id: '',
    crop_rows: [emptyCrop()],
    notes: ''
  })

  useEffect(() => {
    supabase.from('crop_cycles').select('id, cycle_name, crop, system, current_stage')
      .not('current_stage', 'eq', 'Complete').order('transplant_date', { ascending: false })
      .then(({ data }) => setActiveCycles(data || []))
    if (tab === 'history') loadLogs()
  }, [tab])

  const loadLogs = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('harvest_logs')
      .select('*, profiles(full_name)')
      .order('harvest_date', { ascending: false })
      .limit(50)
    setLogs(data || [])
    setLoading(false)
  }

  const setCropRow = (i, field, val) => {
    const rows = [...form.crop_rows]
    rows[i] = { ...rows[i], [field]: val }
    setForm(f => ({ ...f, crop_rows: rows }))
  }

  const totalA = form.crop_rows.reduce((s, r) => s + (parseFloat(r.grade_a_kg) || 0), 0)
  const totalB = form.crop_rows.reduce((s, r) => s + (parseFloat(r.grade_b_kg) || 0), 0)
  const totalR = form.crop_rows.reduce((s, r) => s + (parseFloat(r.reject_kg) || 0), 0)

  const handleSave = async () => {
    const validRows = form.crop_rows.filter(r => r.crop_id && (r.grade_a_kg || r.grade_b_kg || r.reject_kg))
    if (!validRows.length) { setError('Please add at least one crop with harvest quantities.'); return }
    setSaving(true); setError(''); setSaved(false)

    const inserts = validRows.map(r => ({
      user_id: user.id,
      harvest_date: form.harvest_date,
      cycle_id: form.cycle_id || null,
      crop_id: r.crop_id,
      total_kg: (parseFloat(r.grade_a_kg)||0) + (parseFloat(r.grade_b_kg)||0) + (parseFloat(r.reject_kg)||0),
      grade_a_kg: parseFloat(r.grade_a_kg) || 0,
      grade_b_kg: parseFloat(r.grade_b_kg) || 0,
      reject_kg: parseFloat(r.reject_kg) || 0,
      count_units: parseInt(r.count) || null,
      notes: r.notes || form.notes
    }))

    const { data: savedHarvests, error: err } = await supabase.from('harvest_logs').insert(inserts).select()
    if (err) { setError(err.message); setSaving(false); return }

    // Auto-enter harvested crops into Stores (stock_movements)
    if (savedHarvests && savedHarvests.length > 0) {
      for (const h of savedHarvests) {
        const cropName = h.crop || 'Crop'
        const gradeAB = (h.grade_a_kg||0) + (h.grade_b_kg||0)

        // 1. Move to Stores as Finished Goods
        await supabase.from('stock_movements').insert({
          user_id: user.id,
          movement_date: h.harvest_date,
          movement_type: 'in',
          source: 'harvest',
          reference_id: h.id,
          item_name: cropName,
          category: 'Produce',
          qty_in: gradeAB,
          qty_out: 0,
          unit: 'kg',
          notes: `Harvest — Grade A: ${h.grade_a_kg}kg, Grade B: ${h.grade_b_kg}kg, Reject: ${h.reject_kg}kg`,
        })

        // 2. WIP → Finished Goods journal entry
        if (gradeAB > 0) {
          await supabase.from('wip_journal').insert({
            entry_date: h.harvest_date,
            type: 'wip_to_finished',
            item_name: cropName,
            quantity: gradeAB,
            unit: 'kg',
            unit_cost: 0,
            crop_name: cropName,
            notes: `Harvested — Grade A: ${h.grade_a_kg}kg, Grade B: ${h.grade_b_kg}kg`,
            user_id: user.id,
          })
        }
      }

      // Mark harvests as entered into stores
      await supabase.from('harvest_logs').update({ entered_stores: true, entered_stores_at: new Date().toISOString() })
        .in('id', savedHarvests.map(h => h.id))
    }

    setSaved(true); setSaving(false)
    setTimeout(() => setSaved(false), 3000)
    setForm({ harvest_date: ugandaDate(), cycle_id: '', crop_rows: [emptyCrop()], notes: '' })
  }

  return (
    <AppLayout title="Harvest Log">
      <div className="section-header">
        <div className="section-icon">🌾</div>
        <div className="section-info">
          <h2>Harvest Log</h2>
          <p>Record daily harvest weights and grades for each crop. Every harvest must be logged.</p>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='new'?'active':''}`} onClick={() => setTab('new')}>📝 Record Harvest</button>
        <button className={`tab ${tab==='history'?'active':''}`} onClick={() => setTab('history')}>📚 History</button>
      </div>

      {tab === 'new' && (
        <div className="card">
          {saved && <div className="alert alert-green">✅ Harvest recorded and automatically entered into Stores!</div>}
          {error && <div className="alert alert-red">❌ {error}</div>}
          {!canEditData && <div className="alert alert-amber">👁 You have view-only access and cannot record harvests.</div>}

          <div className="grid-2" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Harvest Date</label>
              <input className="form-input" type="date" value={form.harvest_date} onChange={e => setForm(f => ({ ...f, harvest_date: e.target.value }))} disabled={!canEditData} />
            </div>
            <div className="form-group">
              <label className="form-label">Logged by</label>
              <input className="form-input" value={profile?.full_name || ''} readOnly style={{ background: '#f0f0e8' }} />
            </div>
          </div>

          {/* Cycle selector */}
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">🌿 Which crop cycle is this harvest from?</label>
            <select className="form-select" value={form.cycle_id} onChange={e => setForm(f => ({ ...f, cycle_id: e.target.value }))} disabled={!canEditData}>
              <option value="">— Select cycle (recommended) —</option>
              {activeCycles.map(c => (
                <option key={c.id} value={c.id}>{c.cycle_name} — {c.crop}{c.variety ? ` (${c.variety})` : ''} · {c.system}{c.zone && c.zone !== 'Full frame' ? ` · ${c.zone}` : ''}</option>
              ))}
            </select>
            {form.cycle_id && (
              <div style={{ fontSize: '0.72rem', color: 'var(--green-mid)', marginTop: 4, fontWeight: 700 }}>
                ✅ This harvest will appear in the {activeCycles.find(c => c.id === form.cycle_id)?.cycle_name} cycle and auto-enter Stores
              </div>
            )}
            {activeCycles.length === 0 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                No active cycles. Start one in <strong>Farm Operations → Crop Cycles</strong>.
              </div>
            )}
          </div>

          {/* Grade Guide */}
          <div style={{ background:'var(--amber-pale)', border:'1px solid var(--amber-light)', borderRadius:8, padding:12, marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:'0.8rem', marginBottom:8, color:'var(--amber)' }}>📋 Grading Guide</div>
            <div className="grid-3" style={{ gap:8 }}>
              {[['✅ Grade A','Full size, perfect condition, no blemishes. Premium price.','var(--green-mid)'],['🟡 Grade B','Slightly small or minor cosmetic issue only. Sell at discount.','var(--amber)'],['❌ Reject','Diseased, damaged, mouldy or unsaleable. Bag & dispose. Do NOT sell.','var(--red)']].map(([label,desc,color])=>(
                <div key={label} style={{ background:'white', borderRadius:6, padding:'8px 10px' }}>
                  <div style={{ fontWeight:700, fontSize:'0.75rem', color, marginBottom:3 }}>{label}</div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', lineHeight:1.5 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="table-wrap" style={{ marginBottom: 10 }}>
            <table>
              <thead>
                <tr><th>Crop</th><th>No. of Units</th><th>Grade A (kg)</th><th>Grade B (kg)</th><th>Reject (kg)</th><th>Total (kg)</th><th>Notes</th><th style={{ width: 32 }}></th></tr>
              </thead>
              <tbody>
                {form.crop_rows.map((row, i) => {
                  const cropName = activeCycles.find(c=>c.id===row.crop_id)?.crop || activeCycles.find(c=>c.id===row.crop_id)?.cycle_name || ''
                  const guide = GRADE_GUIDE[cropName] || GRADE_GUIDE.default
                  const total = (parseFloat(row.grade_a_kg)||0) + (parseFloat(row.grade_b_kg)||0) + (parseFloat(row.reject_kg)||0)
                  return (
                    <React.Fragment key={i}>
                      <tr>
                        <td>
                          <select className="form-select" value={row.crop_id} onChange={e => setCropRow(i, 'crop_id', e.target.value)} disabled={!canEditData}>
                            <option value="">Select crop cycle...</option>
                            {activeCycles.map(c => <option key={c.id} value={c.id}>{c.cycle_name} — {c.crop}</option>)}
                          </select>
                        </td>
                        <td>
                          <input className="form-input" style={{width:80}} type="number" placeholder="e.g. 24" value={row.count} onChange={e => setCropRow(i, 'count', e.target.value)} disabled={!canEditData} title="Number of individual plants/heads harvested" />
                        </td>
                        {['grade_a_kg','grade_b_kg','reject_kg'].map(f => (
                          <td key={f}><input className="form-input" type="number" step="0.1" placeholder="0" value={row[f]} onChange={e => setCropRow(i, f, e.target.value)} disabled={!canEditData} /></td>
                        ))}
                        <td><input className="form-input" value={total > 0 ? total.toFixed(2) : ''} readOnly style={{ fontWeight: 700, background: '#f8f8f0' }} /></td>
                        <td><input className="form-input" placeholder="Optional" value={row.notes} onChange={e => setCropRow(i, 'notes', e.target.value)} disabled={!canEditData} /></td>
                        <td>{form.crop_rows.length > 1 && canEditData && <button className="btn-del-row" onClick={() => setForm(f => ({ ...f, crop_rows: f.crop_rows.filter((_,j) => j!==i) }))}>✕</button>}</td>
                      </tr>
                      {row.crop_id && (
                        <tr style={{ background:'var(--cream)' }}>
                          <td colSpan={8} style={{ fontSize:'0.7rem', color:'var(--text-muted)', padding:'4px 12px', fontStyle:'italic' }}>
                            {guide.a} &nbsp;|&nbsp; {guide.b} &nbsp;|&nbsp; {guide.r}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          {canEditData && <button className="btn btn-secondary btn-sm" style={{ marginBottom: 16 }} onClick={() => setForm(f => ({ ...f, crop_rows: [...f.crop_rows, emptyCrop()] }))}>+ Add Another Crop</button>}

          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, background: 'var(--green-pale)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            {[['Grade A', totalA, 'var(--green-mid)'], ['Grade B', totalB, 'var(--amber)'], ['Reject', totalR, 'var(--red)'], ['Total', totalA+totalB+totalR, 'var(--green-deep)']].map(([label, val, color]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.65rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.4rem', fontWeight: 700, color }}>{val.toFixed(2)} kg</div>
              </div>
            ))}
          </div>

          <div className="form-group">
            <label className="form-label">General Notes</label>
            <textarea className="form-textarea" style={{ minHeight: 60 }} placeholder="Any notes about today's harvest..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} disabled={!canEditData} />
          </div>

          {canEditData && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner" /> Saving...</> : '💾 Save Harvest'}
              </button>
              <button className="btn btn-ghost" onClick={() => window.print()}>🖨️ Print</button>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="card">
          <div className="card-title">Harvest History</div>
          {loading ? <div style={{ textAlign: 'center', padding: 32 }}><span className="spinner dark" /></div>
          : logs.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🌾</div><h3>No harvests recorded yet</h3></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Crop</th><th>Units</th><th>Grade A (kg)</th><th>Grade B (kg)</th><th>Reject (kg)</th><th>Total (kg)</th><th>Logged By</th></tr></thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td style={{ fontWeight: 600 }}>{new Date(log.harvest_date).toLocaleDateString('en-UG')}</td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--green-bright)' || 'var(--green-bright)', display: 'inline-block' }} />
                          { '—'}
                        </span>
                      </td>
                      <td style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>{log.count_units ? `${log.count_units} units` : '—'}</td>
                      <td style={{ color: 'var(--green-mid)', fontWeight: 600 }}>{log.grade_a_kg}</td>
                      <td style={{ color: 'var(--amber)' }}>{log.grade_b_kg}</td>
                      <td style={{ color: 'var(--red)' }}>{log.reject_kg}</td>
                      <td style={{ fontWeight: 700 }}>{log.total_kg}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{log.profiles?.full_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                {/* Crop totals summary */}
                {logs.length > 0 && (() => {
                  const totals = {}
                  logs.forEach(l => {
                    const name = l.crops?.name || 'Other'
                    if (!totals[name]) totals[name] = { a:0, b:0, r:0, total:0, color: l.crops?.color }
                    totals[name].a += parseFloat(l.grade_a_kg||0)
                    totals[name].b += parseFloat(l.grade_b_kg||0)
                    totals[name].r += parseFloat(l.reject_kg||0)
                    totals[name].total += parseFloat(l.total_kg||0)
                  })
                  return (
                    <tfoot>
                      <tr><td colSpan={8} style={{ padding:'8px 12px', fontWeight:700, fontSize:'0.75rem', color:'var(--text-muted)', background:'var(--cream)', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.1em' }}>Summary by Crop (all records above)</td></tr>
                      {Object.entries(totals).map(([name, t]) => (
                        <tr key={name} style={{ background:'var(--cream)' }}>
                          <td colSpan={2} style={{ fontWeight:700, fontSize:'0.85rem' }}>
                            <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                              <span style={{ width:10, height:10, borderRadius:'50%', background:t.color||'var(--green-bright)', display:'inline-block' }} />{name}
                            </span>
                          </td>
                          <td></td>
                          <td style={{ color:'var(--green-mid)', fontWeight:600 }}>{t.a.toFixed(2)} kg</td>
                          <td style={{ color:'var(--amber)', fontWeight:600 }}>{t.b.toFixed(2)} kg</td>
                          <td style={{ color:'var(--red)', fontWeight:600 }}>{t.r.toFixed(2)} kg</td>
                          <td style={{ fontWeight:700 }}>{t.total.toFixed(2)} kg</td>
                          <td></td>
                        </tr>
                      ))}
                    </tfoot>
                  )
                })()}
              </table>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  )
}
